/**
 * Connection and freshness state of the live RFIMS feed, for the status chip
 * and the staleness banner. Written by liveFeed.ts, read by the UI. Readings
 * themselves go to useTwin; this store only says where they come from and
 * how old they are.
 */
import { create } from 'zustand';
import type { LiveDataSource, LiveSite, LiveSiteMetrics, LiveStatusPayload } from './types';

export type LiveConnection = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'polling' | 'closed';

export interface LiveFeedState {
  connection: LiveConnection;
  /** Site the feed is driving (the open rfims element), or null when off. */
  siteId: string | null;
  /** 'influxdb' for real readings, 'stub' for the Worker's synthetic data, null before the first payload. */
  source: LiveDataSource | null;
  upstreamHost: string | null;
  degradedReason: string | null;
  /** ISO time of the newest Influx point for the site, from the last payload. */
  lastSeen: string | null;
  lastSeenMs: number | null;
  /** ms when the last payload arrived (client clock). */
  lastPayloadAt: number | null;
  /** True once a payload is in hand and lastSeen is missing or older than two minutes. */
  stale: boolean;
  /** Raw metrics of the site from the last payload (the old bridge's latest(metric), used for live limits in forecasts). */
  metrics: LiveSiteMetrics | null;
  identityStatus: LiveSite['identityStatus'] | null;

  setConnection: (connection: LiveConnection) => void;
  setSite: (siteId: string | null) => void;
  applyPayload: (payload: LiveStatusPayload, site: LiveSite | null, lastSeenMs: number | null, nowMs: number) => void;
  setStale: (stale: boolean) => void;
  reset: () => void;
}

const initial = {
  connection: 'idle' as LiveConnection,
  siteId: null,
  source: null,
  upstreamHost: null,
  degradedReason: null,
  lastSeen: null,
  lastSeenMs: null,
  lastPayloadAt: null,
  stale: false,
  metrics: null,
  identityStatus: null,
};

export const useLiveFeed = create<LiveFeedState>()((set) => ({
  ...initial,
  setConnection: (connection) => set({ connection }),
  setSite: (siteId) => set({ siteId }),
  applyPayload: (payload, site, lastSeenMs, nowMs) =>
    set({
      source: payload._source ?? null,
      upstreamHost: payload._upstream?.host ?? null,
      degradedReason: payload._degraded?.reason ?? null,
      lastSeen: site?.lastSeen ?? null,
      lastSeenMs,
      lastPayloadAt: nowMs,
      metrics: payload._source === 'influxdb' ? site?.metrics ?? null : null,
      identityStatus: site?.identityStatus ?? null,
    }),
  setStale: (stale) => set({ stale }),
  reset: () => set({ ...initial }),
}));

/** Latest raw value of a metric for the site the feed drives, or null when unknown (the old bridge's latest()). */
export function latestLiveMetric(metric: keyof LiveSiteMetrics): number | null {
  const value = useLiveFeed.getState().metrics?.[metric];
  return typeof value === 'number' && !Number.isNaN(value) ? value : null;
}
